import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '@/hooks'
import { fetchLoadEmails, sendEmailReply } from '../api/emailApi'
import { EmailData } from '../types/email'
import { isBrokerReplyNotification } from '../utils/notifications'

interface UseEmailChatReturn {
    emails: EmailData[]
    pendingEmails: EmailData[]
    loading: boolean
    sending: boolean
    error: string | null
    refreshEmails: () => Promise<void>
    /** Resolves true once the server accepted the reply, false if it failed or was skipped (no thread / already sending). */
    sendReply: (htmlBody: string, to: string, subject: string) => Promise<boolean>
    lastMessageId: string | undefined
}

export const useEmailChat = (loadId: string, emailThreadId?: string, isExpanded?: boolean): UseEmailChatReturn => {
    const [emails, setEmails] = useState<EmailData[]>([])
    const [pendingEmails, setPendingEmails] = useState<EmailData[]>([])
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const mountedRef = useRef(true)
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Track mount state and cleanup timers
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current)
                refreshTimerRef.current = null
            }
        }
    }, [])

    const refreshEmails = useCallback(async () => {
        if (!emailThreadId || !loadId || !mountedRef.current) return
        setLoading(true)
        setError(null)
        try {
            const response = await fetchLoadEmails(emailThreadId, loadId)
            if (!mountedRef.current) return
            const fetched = response?.data?.emails || response?.data || []
            setEmails(Array.isArray(fetched) ? fetched : [])
            // Clear pending emails that have been confirmed by the server
            setPendingEmails([])
        } catch (err) {
            if (!mountedRef.current) return
            setError('Failed to load emails')
            console.error('Failed to fetch emails:', err)
        } finally {
            if (mountedRef.current) setLoading(false)
        }
    }, [emailThreadId, loadId])

    useEffect(() => {
        if (isExpanded && emailThreadId) {
            refreshEmails()
        }
    }, [isExpanded, emailThreadId, refreshEmails])

    // Real-time reply: the broker-reply WSS handler pushes a "New email received" notification into
    // the store (email-service load-reply chain). When a newer one arrives while this chat is open,
    // refetch so the reply appends live — no per-thread socket needed. The notification carries no
    // thread id, so any reply refetches the open thread (a cheap no-op when nothing changed).
    const notifications = useAppSelector((s) => s.notification.notifications)
    const lastReplyTsRef = useRef<number | null>(null)
    useEffect(() => {
        if (!isExpanded || !emailThreadId) return
        let newest = 0
        for (const n of notifications) {
            if (isBrokerReplyNotification(n.message) && (n.timestamp || 0) > newest) {
                newest = n.timestamp || 0
            }
        }
        // First run establishes a baseline so pre-existing notifications don't trigger a refetch.
        if (lastReplyTsRef.current === null) {
            lastReplyTsRef.current = newest
            return
        }
        if (newest > lastReplyTsRef.current) {
            lastReplyTsRef.current = newest
            void refreshEmails()
        }
    }, [notifications, isExpanded, emailThreadId, refreshEmails])

    // Get the last message ID for reply threading
    const lastMessageId = emails.length > 0 ? emails[emails.length - 1]?.emailData?.object?.id : undefined

    const sendReply = useCallback(
        async (htmlBody: string, to: string, subject: string) => {
            if (!emailThreadId || sending) return false

            const pendingEmail: EmailData = {
                emailData: {
                    object: {
                        subject,
                        body: '',
                        date: Date.now() / 1000,
                        from: [],
                        id: `pending-${Date.now()}`,
                    },
                    htmlBody,
                    subject,
                    to: { email: to },
                },
                isPending: true,
                isSent: false,
            }

            setPendingEmails((prev) => [...prev, pendingEmail])
            setSending(true)

            try {
                await sendEmailReply(emailThreadId, { to: { email: to }, subject, htmlBody, messageId: lastMessageId })
                if (!mountedRef.current) return true
                setPendingEmails((prev) => prev.map((e) => (e === pendingEmail ? { ...e, isSent: true, isPending: false } : e)))
                // Refresh to get server-confirmed emails (with cleanup on unmount)
                refreshTimerRef.current = setTimeout(() => {
                    refreshTimerRef.current = null
                    if (mountedRef.current) refreshEmails()
                }, 2000)
                return true
            } catch (err) {
                if (!mountedRef.current) return false
                setPendingEmails((prev) => prev.map((e) => (e === pendingEmail ? { ...e, isFailed: true, isPending: false } : e)))
                console.error('Failed to send email reply:', err)
                return false
            } finally {
                if (mountedRef.current) setSending(false)
            }
        },
        [emailThreadId, sending, refreshEmails, lastMessageId]
    )

    return { emails, pendingEmails, loading, sending, error, refreshEmails, sendReply, lastMessageId }
}
